// utils/buildSalesOrderPayload.ts

import { SalesOrderDataRetrive } from "Common/CommonEnquiryData/SalesOrderRetriveProductType";
import { parse } from "date-fns";
import { convertLocalDateToUTC } from "helpers/DateUtils/dateTimezoneUtils";
import {
  SALES_ORDER_CATEGORY,
  SALES_ORDER_TYPE,
} from "Constants/StatusConstant";
import { inspect } from "util";

export const buildSalesOrderPayload = (
  formData: SalesOrderDataRetrive,
  items: any[],
  // type: "product" | "service" = "product",
  action: "draft" | "save",
  shippingAddresses?: {
    id: string;
    address: string;
    state?: string;
    city?: string;
    pin_code?: string;
    country?: string;
    phone_number?: string;
  }[],
  organizationsState?: any[],
  isEdit: boolean = false
) => {
  // Helper to convert date string to UTC ISO string
  // Handles yyyy-MMM-dd format (e.g., "2026-Jan-14") and YYYY-MM-DD format
  // Converts local date to UTC ISO string for backend
  const convertToUTC = (dateStr: string | Date | null | undefined): string => {
    if (!dateStr) return "";
    
    // If it's a Date object, convert directly
    if (dateStr instanceof Date) {
      if (isNaN(dateStr.getTime())) return "";
      return dateStr.toISOString();
    }
    
    // If it's a string
    if (typeof dateStr === "string") {
      // If already UTC ISO string, return as is
      if (dateStr.includes("T") || dateStr.includes("Z")) {
        return dateStr;
      }
      
      // If empty string or invalid, return empty
      if (dateStr.trim() === "" || dateStr === "Invalid Date") return "";
      
      try {
        let localDateStr = "";
        
        // Try parsing yyyy-MMM-dd format first (e.g., "2026-Jan-14")
        let parsed = parse(dateStr, "yyyy-MMM-dd", new Date());
        if (!isNaN(parsed.getTime())) {
          // Convert to YYYY-MM-DD format
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          localDateStr = `${year}-${month}-${day}`;
        }
        // Try parsing YYYY-MM-DD format (e.g., "2026-01-14")
        else if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          localDateStr = dateStr;
        }
        
        // Convert local date string to UTC ISO string
        if (localDateStr) {
          return convertLocalDateToUTC(localDateStr);
        }
        
        return "";
      } catch (error) {
        return "";
      }
    }
    
    return "";
  };

  // Retention details (optional)
  const retention = (formData as any)?.retention_details || {};
  const retention_details = {
    retention_applicable: !!(retention?.retention_applicable ?? retention?.is_applicable),
    retention_type: retention?.retentionType || retention?.retention_type || "",
    retention_percentage:
      retention?.percentage === "" || retention?.percentage === null || retention?.percentage === undefined
        ? ""
        : String(retention?.percentage),
    retention_amount:
      retention?.retentionAmount === "" || retention?.retentionAmount === null || retention?.retentionAmount === undefined
        ? ""
        : String(retention?.retentionAmount),
    next_follow_up_date: convertToUTC(retention?.followUpDate || retention?.next_follow_up_date || retention?.follow_up_date),
  };

  // Get company data
  const company = formData?.company || {};
  const companyId = company?.id || company?.company_id || "";
  const companyName = formData?.company_name || company?.company_name || "";
  const companyEmail = formData?.company_email || "";
  const industry = company?.industry || formData?.industry || "";
  const vendorCode = company?.vendor_code || (formData as any)?.vendor_code || "";
  const branchName = company?.branch_name || (formData as any)?.branch_name || "";
  
  // Get company details from the original company object (may have gstin, gst_type, phone_number)
  // company is already an object, so use it directly
  const companyData = company;
  
  // Get billing address
  const billingAddr = formData?.billing_address || {};
  const billingAddress = {
    city: billingAddr?.city || "",
    state: billingAddr?.state || "",
    address: billingAddr?.address || "",
    country: billingAddr?.country || "",
    pincode: billingAddr?.pin_code || "",
    phone_number: billingAddr?.phone_number || "",
  };

  // Transform contact details
  const contactDetails = (formData?.contactPersonArray || company?.contact_details || []).map((contact: any) => ({
    contact_id: contact?.contact_id || contact?.id || "",
    company_id: companyId,
    name: contact?.contact_person_name || contact?.name || "",
    designation: contact?.contact_person_designation || contact?.designation || "",
    email: contact?.contact_person_email || contact?.email || "",
    phone_number: contact?.contact_person_phone_no || contact?.phone_number || "",
  }));

  // Get shipping addresses
  const shippingAddress = (shippingAddresses || []).map((addr: any) => ({
    shipping_address_id: addr?.id || addr?.shipping_address_id || "",
    address: addr?.address || "",
    city: addr?.city || "",
    state: addr?.state || "",
    country: addr?.country || "",
    pin_code: addr?.pin_code || addr?.pincode || "",
    phone_number: addr?.phone_number || "",
  }));

  // Get bill_from (organization) - full object
  let billFrom = null;
  if (formData?.bill_from_address) {
    if (typeof formData.bill_from_address === "object" && formData.bill_from_address !== null) {
      const billFromAddr = formData.bill_from_address as any;
      if (billFromAddr.organization_id) {
        // Full organization object
        billFrom = {
          organization_id: billFromAddr.organization_id || "",
          organization_name: billFromAddr.organization_name || billFromAddr.org_name || "",
          org_address: billFromAddr.org_address || billFromAddr.address || "",
          industry_type: billFromAddr.industry_type || "",
          org_city: billFromAddr.org_city || billFromAddr.city || "",
          org_state: billFromAddr.org_state || billFromAddr.state || "",
          org_country: billFromAddr.org_country || billFromAddr.country || "",
          org_pincode: billFromAddr.org_pincode || billFromAddr.pin_code || billFromAddr.pincode || "",
          org_status: billFromAddr.org_status || false,
          is_active: billFromAddr.is_active !== undefined ? billFromAddr.is_active : true,
          is_deleted: billFromAddr.is_deleted !== undefined ? billFromAddr.is_deleted : false,
          created_at: billFromAddr.created_at || "",
          updated_at: billFromAddr.updated_at || "",
          created_by: billFromAddr.created_by || "",
          updated_by: billFromAddr.updated_by || "",
        };
      } else {
        // Simple address object
        billFrom = {
          city: billFromAddr.city || "",
          state: billFromAddr.state || "",
          address: billFromAddr.address || billFromAddr.org_address || "",
          country: billFromAddr.country || "",
          pincode: billFromAddr.pin_code || billFromAddr.pincode || "",
          phone_number: billFromAddr.phone_number || "",
        };
      }
    } else if (organizationsState && organizationsState.length > 0) {
      // Find organization from state
      const orgObj = organizationsState.find(
        (org: any) => org.org_address === formData.bill_from_address || org.organization_id === formData.bill_from_address
      );
      if (orgObj) {
        billFrom = {
          organization_id: orgObj.organization_id || "",
          organization_name: orgObj.organization_name || "",
          org_address: orgObj.org_address || "",
          industry_type: orgObj.industry_type || "",
          org_city: orgObj.org_city || orgObj.city || "",
          org_state: orgObj.org_state || orgObj.state || "",
          org_country: orgObj.org_country || orgObj.country || "",
          org_pincode: orgObj.org_pincode || orgObj.pin_code || orgObj.pincode || "",
          org_status: orgObj.org_status || false,
          is_active: orgObj.is_active !== undefined ? orgObj.is_active : true,
          is_deleted: orgObj.is_deleted !== undefined ? orgObj.is_deleted : false,
          created_at: orgObj.created_at || "",
          updated_at: orgObj.updated_at || "",
          created_by: orgObj.created_by || "",
          updated_by: orgObj.updated_by || "",
        };
      }
    }
  }

  // Get bill_to (billing address)
  const billTo = billingAddress;

  // Get agent name
  const agentName = formData?.agent_name || "";

  // Get assigned members (full user objects)
  const assignedMembers = (formData?.members || []).map((m: any) => ({
    user_id: m?.user_id || m?.id || m?.user || "",
    username: m?.username || "",
    first_name: m?.first_name || "",
    last_name: m?.last_name || "",
    email: m?.email || m?.user_email || "",
    designation: m?.designation || "",
    address: m?.address || "",
    city: m?.city || "",
    state: m?.state || "",
    country: m?.country || "",
    pin_code: m?.pin_code || m?.pincode || "",
    mobile_number: m?.mobile_number || m?.phone_number || "",
    user_type: m?.user_type || "",
  }));

  // Get currency details
  const currencyCode = formData?.currency_code || "INR";
  const currencySymbol = formData?.currency_symbol || "";
  const currencyRate = formData?.exchange_rate || "";
  const currencyApplicable = (formData as any)?.currency_applicable ?? false;
  // Get EMD details (if available in formData)
  const modeOfEmd = (formData as any)?.mode_of_emd || "";
  const emd = (formData as any)?.emd || "";
  const emdAmount = (formData as any)?.emd_amount || "";
  const emdApplicable = ((formData as any)?.emd_applicable);
  const paymentTermsAmount = (formData as any)?.payment_terms_amount;
  const payment_terms_amount =
    paymentTermsAmount !== null && paymentTermsAmount !== undefined && paymentTermsAmount !== "" && String(paymentTermsAmount).trim() !== ""
      ? (typeof paymentTermsAmount === "string" ? parseFloat(paymentTermsAmount) : paymentTermsAmount)
      : "";

  // Get inspection/installation
  const inspection = formData?.inspection || "";
  const inspectionDate = convertToUTC(formData?.inspection_date);
  const installation = formData?.installation || "";
  const installationDate = convertToUTC(formData?.installation_date);
  const inspectionApplicable = (formData as any)?.inspection_applicable ?? false;
  // Get PBG details
  const pbgDetails = formData?.pbg_details;
  const pbgApplicable = pbgDetails?.pbg_required || false;
  const pbgNumber = pbgDetails?.pbg_number || "";
  const pbgDate = convertToUTC(pbgDetails?.pbg_date);
  const bankName = pbgDetails?.bank_name || "";
  const pbgAmount = String(pbgDetails?.amount || "");
  // Handle pbg_validity: if it's a valid date, convert to UTC; otherwise send null
  // The form allows text input like "12 months", but backend expects DateTime or null
  // Match the logic from ServiceSalesOrderPayload.tsx
  let pbgValidityFormatted: string | null = null;
  const pbgValidityConverted = convertToUTC(pbgDetails?.validity);
  if (pbgValidityConverted && pbgValidityConverted.trim() !== "") {
    // convertToUTC returned a valid date string
    pbgValidityFormatted = pbgValidityConverted;
  } else {
    // Try to parse as Date to check if it's a valid date
    const dateTry = new Date(pbgDetails?.validity || "");
    if (!isNaN(dateTry.getTime())) {
      pbgValidityFormatted = dateTry.toISOString();
    } else {
      // Not a valid date (e.g., "12 months"), send null instead of empty string
      pbgValidityFormatted = null;
    }
  }
  const pbgValidity = pbgValidityFormatted;
  const bgStatus = pbgDetails?.pbg_status || "";
  // Store raw pbg_documents for file extraction
  const pbgDocumentsRaw = pbgDetails?.upload_document || [];

  // Get terms and conditions - transform from form format (description with newlines) to backend format (array of {type, options})
  let termsAndConditions: any[] = [];
  if (formData?.terms_and_conditions) {
    if (typeof formData.terms_and_conditions === "string") {
      // Try to parse if it's JSON string
      try {
        const parsed = JSON.parse(formData.terms_and_conditions);
        termsAndConditions = Array.isArray(parsed) ? parsed : [];
      } catch {
        // If not JSON, create array with single entry
        termsAndConditions = [{ type: "other", options: formData.terms_and_conditions }];
      }
    } else if (Array.isArray(formData.terms_and_conditions)) {
      // Transform each term: if description has newlines, split into multiple entries
      termsAndConditions = formData.terms_and_conditions.flatMap((term: any) => {
        if (!term.type) return [];
        
        // If description exists and contains newlines, split into multiple options
        if (term.description && term.description.includes("\n")) {
          const options = term.description.split("\n").filter((opt: string) => opt.trim() !== "");
          // Create one entry per option with the same type
          return options.map((option: string) => ({
            type: term.type,
            options: option.trim(),
          }));
        } else {
          // Single option - use description or option field
          const option = term.description || term.option || "";
          if (option.trim() === "") return [];
          return [{
            type: term.type,
            options: option.trim(),
          }];
        }
      });
    }
  }

  // Get totals
  const subtotalBeforeDiscount = formData?.subtotal_before_discount || "0";
  const totalItemDiscounts = formData?.total_item_discounts || "0";
  const totalGstValue = formData?.total_gst || "0";
  const netTotal = formData?.net_taxable_value || "0";
  const grandTotalAmount = formData?.total_amount || "0";

  // Build sales_order_details
  const sales_order_details = {
    sales_order_date: convertToUTC(formData?.sales_order_date),
    valid_until: convertToUTC(formData?.valid_until_date),
    initiated_by: formData?.initiated_by || "",
    reference_number: formData?.reference_number || "",
    quotation_product_id: formData?.quotation || "",
    quotation_number: formData?.quotation_number || "",
    quotation: formData?.quotation || "",
    enquiry_product_id: formData?.enquiry || "",
    enquiry_number: formData?.enquiry_number || "",
    enquiry: formData?.enquiry || "",
    sales_order_stage: action === "draft" ? "draft" : "orders_log",
    source_of_sales_order: (formData as any)?.source_of_sales_order || "",
    description: formData?.notes || "",
  };

  // Get customer PO details
  const customerPoNumber = (formData as any)?.customer_po_number || "";
  const customerPoDate = convertToUTC((formData as any)?.customer_po_date);
  
  // Extract File objects from customer_po_document_url
  const customerPoDocumentsArray = Array.isArray(formData?.customer_po_document_url) ? formData.customer_po_document_url : [];
  const customerPoDocumentFiles: File[] = customerPoDocumentsArray.filter((doc: any) => doc instanceof File) as File[];
  const customerPoDocumentUrls: string[] = customerPoDocumentsArray.filter((doc: any) => typeof doc === "string") as string[];
  
  // Build customer_details
  // Try to get gstin and gst_type from company object or formData
  const gstin = (companyData as any)?.gstin || (formData as any)?.gstin || "";
  const gstType = (companyData as any)?.gst_type || (formData as any)?.gst_type || "";
  const companyPhoneNumber = (companyData as any)?.phone_number || (formData as any)?.phone_number || formData?.contact_person_phone_no || "";
  
  const customer_details = {
    gstin: gstin,
    gst_type: gstType,
    industry: industry,
    company_id: companyId,
    company_name: companyName,
    vendor_code: vendorCode,
    branch_name: branchName,
    sr_no: (companyData as any)?.sr_no || (formData as any)?.sr_no || 1,
    phone_number: companyPhoneNumber,
    email: companyEmail,
    address: billingAddress.address || `${billingAddress.city}, ${billingAddress.state}, ${billingAddress.country} ${billingAddress.pincode}`,
    contact_details: contactDetails,
    billing_address: billingAddress,
    shipping_address: shippingAddress,
    customer_po_number: customerPoNumber,
    customer_po_date: customerPoDate,
    customer_po_documents: customerPoDocumentUrls, // Only URLs, File objects handled separately
  };

  // Build sales_order_products
  const sales_order_products = items.map((item: any) => {
    // Get product type array if available - handle multiple formats
    let productTypeArray: any[] = [];
    const productType = item?.product_type || item?.type || [];
    
    if (Array.isArray(productType) && productType.length > 0) {
      // Check if already in the correct format: [{ type: { type_name, type_id } }]
      if (productType[0]?.type && productType[0].type.type_name) {
        productTypeArray = productType;
      } else {
        // Transform from flat array or different structure
        productTypeArray = productType.map((t: any) => {
          // If t already has type property, use it
          if (t?.type) {
            return {
              type: {
                type_name: t.type.type_name || t.type.name || "",
                type_id: t.type.type_id || t.type.id || "",
              },
            };
          }
          // Otherwise, create the structure
          return {
            type: {
              type_name: t?.type_name || t?.name || "",
              type_id: t?.type_id || t?.id || "",
            },
          };
        });
      }
    }

    // Get shipping address for this product
    let shippingAddress = null;
    if (item?.shipping_address_id && shippingAddresses && shippingAddresses.length > 0) {
      const shippingAddr = shippingAddresses.find(
        (addr) => addr.id === item.shipping_address_id
      );
      if (shippingAddr) {
        shippingAddress = {
          shipping_address_id: shippingAddr.id || "",
          address: shippingAddr.address || "",
          city: shippingAddr.city || "",
          state: shippingAddr.state || "",
          country: shippingAddr.country || "",
          pin_code: shippingAddr.pin_code || "",
          phone_number: shippingAddr.phone_number || "",
        };
      }
    } else if (item?.shipping_address && typeof item.shipping_address === "object") {
      // If shipping_address is already an object in the item
      const itemShippingAddr = item.shipping_address as any;
      shippingAddress = {
        shipping_address_id: itemShippingAddr.shipping_address_id || itemShippingAddr.id || "",
        address: itemShippingAddr.address || "",
        city: itemShippingAddr.city || "",
        state: itemShippingAddr.state || "",
        country: itemShippingAddr.country || "",
        pin_code: itemShippingAddr.pin_code || itemShippingAddr.pincode || "",
        phone_number: itemShippingAddr.phone_number || "",
      };
    }

    // Get discount values
    const discountType = item?.discount_type || "none";
    
    // Handle discount_value based on discount_type
    let discountValue: string;
    if (discountType === "none") {
      discountValue = "0";
    } else if (discountType === "%" || discountType === "percentage") {
      // For percentage, use discount_percent if available, otherwise discount_value
      discountValue = String(item?.discount_percent ?? item?.discount_value ?? "0");
    } else {
      // For currency/flat discount, use discount_value directly (don't fall back to discount_percent)
      discountValue = String(item?.discount_value ?? "0");
    }
    
    // Get discount_percent - only include when discount_type is "%"
    const discountPercent = discountType === "%" || discountType === "percentage"
      ? (item?.discount_percent !== undefined && item?.discount_percent !== null 
          ? String(item.discount_percent) 
          : (item?.discount !== undefined && item?.discount !== null 
              ? String(item.discount) 
              : null))
      : null;

    // Get GST percentages
    const igstPercentage = item?.igst_percent || item?.igst_percentage || null;
    const cgstPercentage = item?.cgst_percent || item?.cgst_percentage || null;
    const sgstPercentage = item?.sgst_percent || item?.sgst_percentage || null;
    console.log("item in buildSalesOrderPayload of product", item);
    return {
      product_id: item?.item?.id || item?.item || item?.product_id || "",
      product_name: item?.item_name || item?.product_name || "",
      category_name: item?.category_name || "",
      sub_category_name: item?.sub_category_name || "",
      make_name: item?.make_name || "",
      model_name: item?.model_name || "",
      unit_name: item?.unit_name || item?.unit?.unit_name || "",
      unit_price: String(item?.unit_price || "0.00"),
      hsn_code: item?.hsn_code || item?.hsn?.hsn_code || "",
      gst_percentage: String(item?.gst_percent || item?.gst_percentage || "0.00"),
      igst_percentage: igstPercentage !== null && igstPercentage !== undefined ? String(igstPercentage) : null,
      cgst_percentage: cgstPercentage !== null && cgstPercentage !== undefined ? String(cgstPercentage) : null,
      sgst_percentage: sgstPercentage !== null && sgstPercentage !== undefined ? String(sgstPercentage) : null,
      remark: item?.remark || "",
      product_sku: item?.product_sku || item?.sku || "",
      product_code: item?.product_code || "",
      product_quantity: Number(item?.quantity || 0),
      product_total: String(item?.total_amount || "0.00"),
      product_net_total: String(item?.net_value || item?.product_net_total || item?.total_price || "0.00"),
      discount_type: discountType,
      discount_percent: discountPercent,
      discount_value: discountValue,
      serviceable: item?.is_serviceable !== undefined ? item.is_serviceable : (item?.serviceable !== undefined ? item.serviceable : false),
      serial_number: item?.serial_number || null,
      shipping_address: shippingAddress || null,
      created_by: (item as any)?.created_by || "",
      updated_by: (item as any)?.updated_by || "",
      product_type: productTypeArray,
      warranty_in_months: item?.warranty_in_months && item.warranty_in_months.toString().trim() !== "" ? item.warranty_in_months.toString() : null,
      warranty_date: item?.warranty_date && item.warranty_date.toString().trim() !== "" ? convertToUTC(item.warranty_date) : null,
      gross_value: item?.gross_value ? String(item.gross_value) : null,
      rate_in_currency: item?.rate_in_currency || item?.rate_in ? String(item.rate_in_currency || item.rate_in) : null,
      item_availability: item?.availability || item?.item_availability || "In Stock",
      gst_value: item?.gst_value ? String(item.gst_value) : null,
      prod_description: item?.prod_description ?? item?.description ?? null,
      additional_details: item?.additional_details ?? null,
    };
  });

  // Extract File objects from attachments and pbg_documents
  const attachmentsArray = Array.isArray(formData?.attachments) ? formData.attachments : [];
  const attachmentFiles: File[] = attachmentsArray.filter((att: any) => att instanceof File) as File[];
  const attachmentUrls: string[] = attachmentsArray.filter((att: any) => typeof att === "string") as string[];

  const pbgDocumentsArray = Array.isArray(pbgDocumentsRaw) ? pbgDocumentsRaw : [];
  const pbgDocumentFiles: File[] = pbgDocumentsArray.filter((doc: any) => doc instanceof File) as File[];
  const pbgDocumentUrls: string[] = pbgDocumentsArray.filter((doc: any) => typeof doc === "string") as string[];

  // Collect all follow-ups into a single array
  const followUps: any[] = [];

  // Add retention follow-up only if retention is applicable and follow-up date is filled
  const retentionFollowUpDate = retention_details?.next_follow_up_date;
  if (
    retention_details?.retention_applicable &&
    retentionFollowUpDate &&
    retentionFollowUpDate.trim()
  ) {
    followUps.push({
      next_follow_up_date: convertToUTC(retentionFollowUpDate),
      follow_up_type: "Initial Follow-up", // Default type for retention
      source_entity_type: SALES_ORDER_TYPE?.PRODUCT, // PRODUCT or SERVICE
      follow_up_category: SALES_ORDER_CATEGORY?.RETENTION,
      follow_up_remarks: "",
    });
  }

  // Add PBG follow-up only if PBG is applicable and follow-up date is filled
  const pbgFollowUpDate = (formData as any)?.pbg_details?.next_follow_up_date;
  if (pbgApplicable && pbgFollowUpDate && pbgFollowUpDate.trim()) {
    followUps.push({
      next_follow_up_date: convertToUTC(pbgFollowUpDate),
      follow_up_type: "Initial Follow-up", // Default type for PBG
      source_entity_type: SALES_ORDER_TYPE?.PRODUCT, // PRODUCT or SERVICE
      follow_up_category: SALES_ORDER_CATEGORY?.PBG,
      follow_up_remarks: "",
    });
  }

  // Add general follow-up from ScheduleFollowUp component only if not in edit mode
  // In edit forms, "Other" follow-up is not needed
  if (!isEdit && (formData as any)?.next_follow_up_date && (formData as any)?.next_follow_up_date.trim()) {
    followUps.push({
      next_follow_up_date: convertToUTC((formData as any)?.next_follow_up_date),
      follow_up_type: "Initial Follow-up", // Default type for general follow-up
      source_entity_type: SALES_ORDER_TYPE?.PRODUCT, // PRODUCT or SERVICE
      follow_up_category: SALES_ORDER_CATEGORY?.OTHERS, // General order follow-up
      follow_up_remarks: String((formData as any)?.follow_up_remarks || ""),
    });
  }

  // Debug logging
  // console.log("buildSalesOrderPayload - formData.attachments:", formData?.attachments);
  // console.log("buildSalesOrderPayload - attachmentFiles:", attachmentFiles);
  // console.log("buildSalesOrderPayload - attachmentUrls:", attachmentUrls);
  // console.log("buildSalesOrderPayload - pbgDocumentsRaw:", pbgDocumentsRaw);
  // console.log("buildSalesOrderPayload - pbgDocumentFiles:", pbgDocumentFiles);
  // console.log("buildSalesOrderPayload - pbgDocumentUrls:", pbgDocumentUrls);
  // console.log("buildSalesOrderPayload - customer_po_document_url:", formData?.customer_po_document_url);
  // console.log("buildSalesOrderPayload - customerPoDocumentFiles:", customerPoDocumentFiles);
  // console.log("buildSalesOrderPayload - customerPoDocumentUrls:", customerPoDocumentUrls);
  // console.log("buildSalesOrderPayload - followUps:", followUps);

  // Build the complete payload with all fields in sales_order_details except sales_order_products
  const payload = {
    sales_order_details: {
      ...sales_order_details,
      customer_details: customer_details,
      agent_name: agentName,
      assigned_members: assignedMembers,
      currency: "",
      currency_symbol: currencySymbol,
      currency_code: currencyCode,
      currency_applicable: currencyApplicable,
      currency_rate_in_inr: currencyRate,
      emd_applicable: !!(formData as any)?.emd_applicable,
      mode_of_emd: modeOfEmd,
      emd: emd,
      emd_amount: emdAmount,
      payment_terms_amount: payment_terms_amount !== "" ? payment_terms_amount : undefined,
       inspection_applicable: inspectionApplicable,
      inspection: inspection,
      inspection_date: inspectionDate,
      installation: installation,
      installation_date: installationDate,
      pbg_applicable: pbgApplicable,
      pbg_number: pbgNumber,
      pbg_date: pbgDate,
      bank_name: bankName,
      pbg_amount: pbgAmount,
      pbg_validity: pbgValidity,
      pbg_status: bgStatus,
      pbg_documents: pbgDocumentUrls, // Only URLs, File objects handled separately
      bill_to: billTo,
      bill_from: billFrom,
      notes: formData?.notes || "",
      attachments: attachmentUrls, // Only URLs, File objects handled separately
      terms_and_conditions: termsAndConditions,
      subtotal_before_discount: String(subtotalBeforeDiscount),
      total_item_discounts: String(totalItemDiscounts),
      total_gst_value: String(totalGstValue),
      net_total: String(netTotal),
      grand_total_amount: String(grandTotalAmount),
      retention_details: retention_details,
      // Tags (ensure array of strings, trimmed, unique; preserve user-entered case)
      tags: Array.isArray((formData as any)?.tags)
        ? Array.from(
            new Set(
              ((formData as any).tags as any[])
                .map((t) => String(t).trim())
                .filter((t) => t.length > 0)
            )
          )
        : [],
    },
    sales_order_products: sales_order_products,
    follow_ups: followUps, // Add consolidated follow-ups array
    action: action, // Move action to top level
    // Attach File objects separately for FormData conversion
    _attachmentFiles: attachmentFiles,
    _pbgDocumentFiles: pbgDocumentFiles,
    _customerPoDocumentFiles: customerPoDocumentFiles,
  };

  return payload;
};
